import { useState, useEffect, useRef } from 'react';

const categories = [
  {
    key: 'ferrous',
    label: 'Ferrous Raw Materials',
    icon: 'ri-stack-line',
    image: 'https://public.readdy.ai/ai/img_res/131d8531d6f0232aa00b815723dceb6b.jpg',
    products: ['Iron Ore Fines', 'Iron Ore Lumps', 'Iron Ore Concentrate', 'Hot Briquetted Iron', 'Pig Iron', 'Ferrous Scrap', 'Coking Coal', 'Metallurgical Coke'],
    desc: 'A comprehensive range of ferrous metal products supporting global steel production and infrastructure development.'
  },
  {
    key: 'nonferrous',
    label: 'Non-Ferrous Metals',
    icon: 'ri-copper-coin-line',
    image: 'https://readdy.ai/api/search-image?query=shiny%20copper%20cathodes%20stacked%20on%20pallet%20at%20industrial%20facility%2C%20aluminum%20ingots%20with%20metallic%20silver%20sheen%2C%20zinc%20and%20lead%20ingots%20arranged%20in%20rows%2C%20polished%20nickel%20cathodes%20close-up%2C%20rich%20metallic%20textures%20on%20dark%20background%2C%20industrial%20metals%20warehouse%20photography%2C%20professional%20commodity%20photo&width=600&height=400&seq=ore-product-nonferrous-001&orientation=landscape',
    products: ['Aluminium (ingots, billets, scrap)', 'Copper (cathodes, concentrates, blister)', 'Zinc (ingots, concentrates)', 'Lead (ingots, concentrates)', 'Nickel (cathodes, briquettes, matte)', 'Tin (ingots, concentrates)'],
    desc: 'High-purity non-ferrous metals essential for electronics, construction, and advanced manufacturing industries.'
  },
  {
    key: 'minerals',
    label: 'Minerals',
    icon: 'ri-box-3-line',
    image: 'https://readdy.ai/api/search-image?query=manganese%20ore%20and%20bauxite%20mineral%20samples%20with%20rich%20earthy%20textures%2C%20chrome%20ore%20and%20fluorspar%20crystals%20on%20dark%20background%2C%20industrial%20mineral%20samples%20arranged%20professionally%2C%20graphite%20chunks%20close-up%2C%20dramatic%20studio%20lighting%20on%20raw%20mineral%20materials%2C%20geology%20specimen%20photography&width=600&height=400&seq=ore-product-minerals-001&orientation=landscape',
    products: ['Manganese Ore', 'Bauxite', 'Chrome Ore', 'Fluorspar', 'Graphite'],
    desc: 'Key minerals sourced globally to support steel production, metallurgical processes, and energy sectors.'
  },
  {
    key: 'energy',
    label: 'Energy',
    icon: 'ri-fire-line',
    image: 'https://readdy.ai/api/search-image?query=massive%20coal%20stockpile%20at%20industrial%20port%20terminal%20with%20conveyor%20belts%20and%20heavy%20machinery%20at%20dusk%2C%20black%20thermal%20coal%20and%20coking%20coal%20chunks%20close-up%20texture%2C%20biomass%20wood%20pellets%20in%20bulk%20storage%20facility%2C%20dark%20industrial%20energy%20commodity%20photography%2C%20dramatic%20moody%20lighting%20with%20deep%20shadows%2C%20professional%20bulk%20commodity%20port%20photography&width=600&height=400&seq=ore-product-energy-001&orientation=landscape',
    products: ['Thermal Coal', 'Coking Coal (Metallurgical Coal)', 'Anthracite Coal', 'Coal Fines & Middlings', 'Wood Pellets (Industrial Grade)', 'Agricultural Biomass Pellets', 'Torrefied Biomass'],
    desc: 'Reliable supply of coal and biomass energy commodities supporting power generation, heating, and the global transition to sustainable fuel sources.'
  },
];

export default function ProductPortfolio() {
  const [active, setActive] = useState('ferrous');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-item').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const current = categories.find((c) => c.key === active) ?? categories[0];

  return (
    <section id="products" className="bg-[#1A1A1A] py-28" ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-4">
          <span className="inline-block border border-[#888888]/40 rounded-full px-6 py-2 text-[#A0A0A0] text-xs uppercase tracking-[0.3em]">
            Product Portfolio
          </span>
        </div>
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-4">
          <h2 className="text-4xl md:text-5xl font-playfair text-white max-w-lg">Our Commodity <em className="text-[#A0A0A0]">Portfolio</em></h2>
          <p className="text-[#999999] text-base max-w-md leading-relaxed">
            A diversified portfolio across four commodity categories, designed for resilience and global market adaptability.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 flex flex-wrap gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActive(cat.key)}
              className={`whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer border ${
                active === cat.key
                  ? 'bg-[#4A4A4A] text-white border-[#4A4A4A]'
                  : 'border-white/20 text-[#CCCCCC] hover:border-[#888888]/60 hover:text-[#A0A0A0]'
              }`}
            >
              <i className={`${cat.icon} text-base`} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Active Category Display */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-white/10">
          {/* Image */}
          <div className="relative h-72 lg:h-auto min-h-[320px]">
            <img
              src={current.image}
              alt={current.label}
              key={current.key}
              className="w-full h-full object-cover object-center"
              style={{ transition: 'opacity 0.4s ease' }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A1520]/40" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-[#4A4A4A] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">{current.label}</span>
            </div>
          </div>

          {/* Products List */}
          <div className="bg-[#252525] p-8 lg:p-10">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#4A4A4A]/30 border border-[#888888]/30 mb-5">
              <i className={`${current.icon} text-[#A0A0A0] text-lg`} />
            </div>
            <h3 className="text-2xl font-playfair text-white mb-3">{current.label}</h3>
            <p className="text-[#999999] text-sm leading-relaxed mb-7">{current.desc}</p>
            <div className="space-y-3">
              {current.products.map((product, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#888888] flex-shrink-0" />
                  <span className="text-[#CCCCCC] text-sm">{product}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
